import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from './components/ui/toaster';
import ErrorBoundary from './components/ErrorBoundary';
import MetaPixelPageViews from './components/MetaPixelPageViews';
import Header from './components/Header';
import JourneyBar from './components/JourneyBar';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import Hero from './components/Hero';
import CarrierSection from './components/CarrierSection';
import FeaturesSection from './components/FeaturesSection';
import TestimonialsSection from './components/TestimonialsSection';
import HowItWorksSection from './components/HowItWorksSection';
import Footer from './components/Footer';
import QuoteForm from './components/QuoteForm';
import TrackingPage from './components/TrackingPage';
import SupportPage from './components/SupportPage';
import AboutPage from './components/AboutPage';
import PrivacyPage from './components/PrivacyPage';
import TermsAndConditions from './components/TermsAndConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import AdminApp from './components/AdminApp';
import CourierToUSAPage from './components/CourierToUSAPage';
import CourierToUKPage from './components/CourierToUKPage';
import CourierToEuropePage from './components/CourierToEuropePage';
import CourierToCanadaPage from './components/CourierToCanadaPage';
import HomeRoutesSection from './components/HomeRoutesSection';
import CourierToAustraliaPage from './components/CourierToAustraliaPage';
import UserDashboard from './components/UserDashboard';
import UserProfileImproved from './components/profile/UserProfileImproved';
import MyShipments from './components/MyShipments';
import WalletDashboard from './components/WalletDashboard';
import WhatsAppButton from './components/WhatsAppButton';
import NotFound from './components/NotFound';
import SeoManager from './components/SeoManager';
import ForcePasswordChangeGate from './components/auth/ForcePasswordChangeGate';
import KYCFormModern from './components/profile/KYCFormModern';
import { useAuth } from './contexts/AuthContext';

const Home = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <Hero />
      <HomeRoutesSection />
      <CarrierSection />
      <FeaturesSection />
      <TestimonialsSection />
      <HowItWorksSection />
      <Footer />
    </div>
  );
};

const QuotePage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Focused booking journey: logo + back only, no site nav */}
      {/* No back action here: the page's own "Back to Quotes" handles stepping
          back, and the logo is the (confirmed) exit to the homepage. */}
      <JourneyBar />
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <QuoteForm />
        </div>
      </div>
    </div>
  );
};

// Clean landing for the static homepage's Login/Sign Up buttons. The Header reads
// ?login=required / ?register=true and opens the auth modal over this branded page.
const AuthLanding = () => {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-orange-50 to-blue-50">
      <Header />
      <div className="flex-1 flex items-center justify-center px-4 text-center">
        <div>
          <img src="/assets/images/whitebglogo.jpeg" alt="SendMyParcel" className="h-16 w-auto mx-auto mb-5" />
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Welcome to SendMyParcel</h1>
          <p className="text-slate-500">Sign in or create an account to start shipping globally.</p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const TrackPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <TrackingPage />
      <Footer />
    </div>
  );
};

const SupportPageWrapper = () => {
  return (
    <>
      <Header />
      <SupportPage />
      <Footer />
    </>
  );
};

const AboutPageWrapper = () => {
  return (
    <>
      <Header />
      <AboutPage />
      <Footer />
    </>
  );
};

const TermsAndConditionsPageWrapper = () => {
  return (
    <>
      <Header />
      <TermsAndConditions />
      <Footer />
    </>
  );
};

const PrivacyPolicyPageWrapper = () => {
  return (
    <>
      <Header />
      <PrivacyPolicy />
      <Footer />
    </>
  );
};

const PrivacyPageWrapper = () => {
  return (
    <>
      <Header />
      <PrivacyPage />
      <Footer />
    </>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Automatically redirect to homepage instead of showing "Please Sign In" message
    return <Navigate to="/" replace />;
  }

  return children;
};

// User Dashboard Page
const DashboardPageWrapper = () => {
  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-gray-50">
        <Header />
        <UserDashboard />
      </div>
    </ProtectedRoute>
  );
};

// User Profile Page
const ProfilePageWrapper = () => {
  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-gray-50">
        <Header />
        <UserProfileImproved />
      </div>
    </ProtectedRoute>
  );
};

// My Shipments Page
const MyShipmentsPageWrapper = () => {
  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <MyShipments />
        </div>
      </div>
    </ProtectedRoute>
  );
};

// Wallet Page
const WalletPageWrapper = () => {
  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-gray-50">
        <Header />
        <WalletDashboard />
      </div>
    </ProtectedRoute>
  );
};

// KYC Page
const KYCPageWrapper = () => {
  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-gray-50">
        <Header />
        <KYCFormModern />
      </div>
    </ProtectedRoute>
  );
};




function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <MetaPixelPageViews />
            <SeoManager />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/quote" element={<QuotePage />} />
              <Route path="/login" element={<AuthLanding />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/track" element={<TrackPage />} />
              <Route path="/support" element={<SupportPageWrapper />} />
              <Route path="/about" element={<AboutPageWrapper />} />
              <Route path="/privacy" element={<PrivacyPageWrapper />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditionsPageWrapper />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPageWrapper />} />
              <Route path="/dashboard" element={<DashboardPageWrapper />} />
              <Route path="/profile" element={<ProfilePageWrapper />} />
              <Route path="/profile/kyc" element={<KYCPageWrapper />} />
              <Route path="/my-shipments" element={<MyShipmentsPageWrapper />} />
              <Route path="/wallet" element={<WalletPageWrapper />} />
              <Route path="/admin/*" element={<AdminApp />} />
              <Route path="/send-courier-from-india-to-usa" element={<><Header /><CourierToUSAPage /><Footer /></>} />
              <Route path="/send-courier-from-india-to-uk" element={<><Header /><CourierToUKPage /><Footer /></>} />
              <Route path="/send-courier-from-india-to-europe" element={<><Header /><CourierToEuropePage /><Footer /></>} />
              <Route path="/send-courier-from-india-to-canada" element={<><Header /><CourierToCanadaPage /><Footer /></>} />
              <Route path="/send-courier-from-india-to-australia" element={<><Header /><CourierToAustraliaPage /><Footer /></>} />
              <Route path="*" element={<><Header /><NotFound /><Footer /></>} />
            </Routes>
            {/* Floating WhatsApp Button - visible on all pages */}
            <WhatsAppButton />
            {/* Admin-created accounts must replace their emailed temporary password */}
            <ForcePasswordChangeGate />
          </BrowserRouter>
          <Toaster />
        </div>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;